import { useFormContext } from 'react-hook-form';
import { useEffect, useRef } from 'react';

import type { WorkflowData } from '../../../workflow';
import { type TaskComponentType } from '../../../workflow/schemaTypes';
import { useDeliveryOptions } from '../../../hooks/useDeliveryOptions';
import { useWorkflow } from '../../../workflow/hooks';
import { getCountryCode } from '../../../utils/searchParams';
import { useMethodValidation } from '../../../hooks/useMethodValidation';
import { ReturnMethodFormView } from './MethodTaskView';

export const ReturnMethodForm: TaskComponentType = () => {
  const { requestId, orderNumber } = useWorkflow();
  const prevSelectedMethodRef = useRef<string | undefined>(undefined);
  const prevSelectedLocationRef = useRef<string | undefined>(undefined);
  const lastValidatedMethodRef = useRef<string | undefined>(undefined);

  // isSettingDefaultLocationRef tracks if the UI is between setting a method
  // and setting a default location. This prevents the initial unwanted call.
  const isSettingDefaultLocationRef = useRef(false);

  const { watch, setValue, register } = useFormContext<WorkflowData>();
  const { isLoading, deliveryOptions } = useDeliveryOptions();
  const { setAndValidateMethod, clearValidation } = useMethodValidation();

  const selectedMethod = watch('returnMethod.type');
  const selectedLocation = watch('returnMethod.location');
  const isAddressRequired = watch('returnMethod.isAddressRequired') === true;

  useEffect(() => {
    const prevSelectedMethod = prevSelectedMethodRef.current;
    const prevSelectedLocation = prevSelectedLocationRef.current;
    const isMethodChange = selectedMethod !== prevSelectedMethod;
    const isLocationChange = selectedLocation !== prevSelectedLocation;

    if (isMethodChange) {
      prevSelectedMethodRef.current = selectedMethod;
      prevSelectedLocationRef.current = selectedLocation;
      if (selectedLocation) {
        setValue('returnMethod.location', undefined, {
          shouldValidate: false,
          shouldDirty: false,
        });
        isSettingDefaultLocationRef.current = true;
      }

      // Clear validation when method changes
      clearValidation();
      lastValidatedMethodRef.current = undefined;
    } else if (isLocationChange) {
      if (prevSelectedLocation !== undefined) {
        clearValidation();
        lastValidatedMethodRef.current = undefined;
      }
      prevSelectedLocationRef.current = selectedLocation;
    }

    const method = deliveryOptions?.find((e) => e.id === selectedMethod);
    const defaultLocation = method?.locations?.[0];
    if (selectedMethod && method && defaultLocation && !selectedLocation) {
      isSettingDefaultLocationRef.current = true;
      setValue('returnMethod.location', defaultLocation?.id);
    }

    if (!selectedMethod) return;

    if (isSettingDefaultLocationRef.current) {
      isSettingDefaultLocationRef.current = false;
      return;
    }

    const hasLocations = method?.locations && method.locations.length > 0;
    const canProceed = !hasLocations || (hasLocations && selectedLocation);

    // Prevent re-validation of the same method
    const methodKey = [
      requestId,
      orderNumber,
      selectedMethod ?? 'none',
      selectedLocation ?? 'none',
    ].join('-');

    if (canProceed && lastValidatedMethodRef.current !== methodKey) {
      lastValidatedMethodRef.current = methodKey;
      setAndValidateMethod();
    }
  }, [
    selectedMethod,
    selectedLocation,
    requestId,
    orderNumber,
    deliveryOptions,
    setValue,
    setAndValidateMethod,
    clearValidation,
  ]);

  const method = deliveryOptions?.find((e) => e.id === selectedMethod);
  const hasLocations = Boolean(
    method?.locations && method.locations.length > 0,
  );

  return (
    <ReturnMethodFormView
      isLoading={isLoading}
      deliveryOptions={deliveryOptions}
      selectedMethod={selectedMethod}
      isAddressRequired={isAddressRequired}
      orderNumber={orderNumber ?? undefined}
      countryCode={getCountryCode()}
      hasLocations={hasLocations}
      register={register}
    />
  );
};
